import React from "react";
import { Link } from "react-router";
import {
  ChatBubbleLeftRightIcon,
  CheckBadgeIcon,
  UsersIcon,
} from "@heroicons/react/24/outline";
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@mindstonehq/ui";
import type {
  CommunityActivityItem,
  CommunityActivitySignal,
  CommunityConnectionsSignal,
  CommunityQuestionsSignal,
} from "~/lib/community-signals-types";
import {
  formatEventMeta,
  formatRoleLabels,
  splitActivityTimeline,
} from "~/lib/profile-celebration";

function activityKey(item: CommunityActivityItem): string {
  return `${item.eventSlug ?? item.eventName}:${item.date}`;
}

function ActivityTitle({ item }: { item: CommunityActivityItem }) {
  if (!item.eventSlug) {
    return <span className="text-foreground">{item.eventName}</span>;
  }

  return (
    <Link
      to={`/events/${item.eventSlug}`}
      className="font-medium text-foreground hover:underline"
    >
      {item.eventName}
    </Link>
  );
}

function ActivityRow({ item }: { item: CommunityActivityItem }) {
  const dateMeta = formatEventMeta(null, item.date);
  const cityMeta = formatEventMeta(item.city, null);
  const roleLabels = formatRoleLabels(item.roles);

  return (
    <li className="flex gap-3 rounded-lg border border-border px-3 py-3">
      <CheckBadgeIcon className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
      <div className="min-w-0 flex-1">
        {dateMeta && (
          <p className="text-xs text-muted-foreground">{dateMeta}</p>
        )}
        <div className="mt-1 text-sm">
          <ActivityTitle item={item} />
        </div>
        <div className="mt-2 flex flex-wrap items-center gap-2">
          {roleLabels.map((label) => (
            <Badge key={label} variant="outline">
              {label}
            </Badge>
          ))}
          {cityMeta && (
            <span className="text-xs text-muted-foreground">{cityMeta}</span>
          )}
        </div>
      </div>
    </li>
  );
}

function CommunitySummaryRow({
  questions,
  connections,
}: {
  questions?: CommunityQuestionsSignal;
  connections?: CommunityConnectionsSignal;
}) {
  if (!questions && !connections) return null;

  return (
    <div className="flex flex-wrap gap-x-6 gap-y-3 rounded-lg bg-muted/30 p-3">
      {questions && (
        <div className="flex items-center gap-2 text-sm">
          <ChatBubbleLeftRightIcon className="size-5 text-muted-foreground" />
          <span>
            <span className="font-semibold text-foreground">
              {questions.count}
            </span>{" "}
            {questions.count === 1 ? "question" : "questions"} asked
            {questions.upvotes > 0 && (
              <span className="text-muted-foreground">
                {" "}
                · {questions.upvotes}{" "}
                {questions.upvotes === 1 ? "upvote" : "upvotes"}
              </span>
            )}
          </span>
        </div>
      )}
      {connections && (
        <div className="flex items-center gap-2 text-sm">
          <UsersIcon className="size-5 text-muted-foreground" />
          <span>
            <span className="font-semibold text-foreground">
              {connections.count}
            </span>{" "}
            {connections.count === 1 ? "connection" : "connections"} made
          </span>
        </div>
      )}
    </div>
  );
}

function ActivityPrompt() {
  return (
    <div className="rounded-lg bg-muted/30 p-3 text-sm text-muted-foreground">
      Attend, host, or speak at a Mindstone event to start your community
      timeline.
    </div>
  );
}

function ActivityPromptCard() {
  return (
    <Card className="bg-muted/30" id="profile-activity">
      <CardHeader>
        <CardTitle className="text-base">Community activity</CardTitle>
      </CardHeader>
      <CardContent className="text-sm text-muted-foreground">
        Attend, host, or speak at a Mindstone event to start your community
        timeline.
      </CardContent>
    </Card>
  );
}

export default function ProfileActivityTimeline({
  activity,
  questions,
  connections,
  isCurrentUser,
}: {
  activity?: CommunityActivitySignal;
  questions?: CommunityQuestionsSignal;
  connections?: CommunityConnectionsSignal;
  isCurrentUser: boolean;
}) {
  const [expanded, setExpanded] = React.useState(false);
  const { visible, hidden } = splitActivityTimeline(activity?.items);
  const hasActivityItems = visible.length > 0 || hidden.length > 0;
  const hasSummary = Boolean(questions || connections);

  if (!hasActivityItems && !hasSummary) {
    return isCurrentUser ? <ActivityPromptCard /> : null;
  }

  return (
    <Card id="profile-activity">
      <CardHeader>
        <CardTitle className="text-base">Community activity</CardTitle>
        <CardDescription>
          Verified Mindstone event participation
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        {hasActivityItems && (
          <ul className="flex flex-col gap-2">
            {visible.map((item) => (
              <ActivityRow key={activityKey(item)} item={item} />
            ))}
          </ul>
        )}

        {hidden.length > 0 && (
          <Collapsible open={expanded} onOpenChange={setExpanded}>
            <CollapsibleTrigger asChild>
              <Button variant="ghost" size="sm" className="w-fit px-0">
                {expanded
                  ? "Hide older events"
                  : `Show ${hidden.length} older events`}
              </Button>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <ul className="mt-2 flex flex-col gap-2">
                {hidden.map((item) => (
                  <ActivityRow key={activityKey(item)} item={item} />
                ))}
              </ul>
            </CollapsibleContent>
          </Collapsible>
        )}

        <CommunitySummaryRow questions={questions} connections={connections} />

        {isCurrentUser && !hasActivityItems && <ActivityPrompt />}
      </CardContent>
    </Card>
  );
}
